import type { CropData } from './ImageCropModal';

// Maximum scale factor to prevent images from becoming invisible
// when zoom is set very high (small crop areas)
const MAX_SCALE_FACTOR = 10;

/**
 * Check if crop data or effects are applied (has actual crop settings or grayscale)
 */
export const hasCropApplied = (crop?: CropData): boolean => {
  if (!crop) return false;
  return !!(crop.croppedAreaPercent || crop.zoom !== 1 || crop.grayscale);
};

/**
 * Aspect ratio of the saved crop rectangle.
 * Pixels win when present; otherwise the percent rectangle; otherwise none.
 */
export function cropAspectRatio(crop?: CropData | null): number | undefined {
  const pixels = crop?.croppedAreaPixels;
  if (pixels?.width && pixels?.height) {
    return pixels.width / pixels.height;
  }
  const percent = crop?.croppedAreaPercent;
  if (percent?.width && percent?.height) {
    return percent.width / percent.height;
  }
  return undefined;
}

/** Width/height for a slot whose frame shape is chosen in the editor. */
export function lockedSlotAspect(aspect?: string | null): number | undefined {
  switch (aspect) {
    case 'square':
      return 1;
    case 'portrait':
      return 3 / 4;
    case 'landscape':
    case '16:9':
      return 16 / 9;
    case '4:3':
      return 4 / 3;
    default:
      return undefined;
  }
}

/**
 * Frame slots: the layout owns the box (hero, split hero, cards, squares).
 * The saved crop is a focal point and zoom inside that box, so the editor
 * and the published page show the same frame.
 *
 * Apply to an img that fills a position:relative, overflow:hidden container.
 */
export const getCroppedImageStyles = (crop?: CropData): React.CSSProperties => {
  const filter = crop?.grayscale ? 'grayscale(100%)' : undefined;
  const zoom = crop?.zoom && crop.zoom > 0 ? crop.zoom : 1;

  if (!crop?.croppedAreaPercent) {
    return {
      objectFit: 'cover',
      width: '100%',
      height: '100%',
      filter,
      ...(zoom !== 1 ? { transform: `scale(${zoom})`, transformOrigin: 'center center' } : {}),
    };
  }

  const { x, y, width, height } = crop.croppedAreaPercent;
  const origin = `${x + width / 2}% ${y + height / 2}%`;

  return {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: origin,
    filter,
    ...(zoom !== 1 ? { transform: `scale(${zoom})`, transformOrigin: origin } : {}),
  };
};

/**
 * Render a cropped image with proper container and image styles.
 * Returns the aspectRatio to use for the container and the imageStyle.
 *
 * Aspect slots: the box takes its shape from the crop. object-fit is fill
 * so the saved rectangle is shown without a second crop.
 */
export const getCroppedImageRenderProps = (
  crop?: CropData
): {
  hasCrop: boolean;
  aspectRatio?: number;
  imageStyle: React.CSSProperties;
} => {
  const grayscaleFilter = crop?.grayscale ? 'grayscale(100%)' : undefined;

  if (!crop?.croppedAreaPercent) {
    return {
      hasCrop: !!crop?.grayscale,
      imageStyle: {
        objectFit: 'cover' as const,
        width: '100%',
        height: '100%',
        filter: grayscaleFilter,
      },
    };
  }

  const { x, y, width, height } = crop.croppedAreaPercent;

  // Clamp scale factors to prevent image from disappearing at extreme zoom levels
  const scaleX = Math.min(100 / width, MAX_SCALE_FACTOR);
  const scaleY = Math.min(100 / height, MAX_SCALE_FACTOR);

  // translate() percentages are relative to element's own size
  const translateX = -x;
  const translateY = -y;

  const aspectRatio = cropAspectRatio(crop);

  return {
    hasCrop: true,
    aspectRatio,
    imageStyle: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: `${scaleX * 100}%`,
      height: `${scaleY * 100}%`,
      maxWidth: 'none',
      maxHeight: 'none',
      objectFit: 'fill' as const,
      transform: `translate(${translateX}%, ${translateY}%)`,
      transformOrigin: 'top left',
      filter: grayscaleFilter,
    },
  };
};
